import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Group, SimpleGrid, Stack, Text, Title } from '@repletoryreach/sdk-ui';
import { Alert, EmptyState, PageHeader, TableShell, type ViewMode } from '@repletoryreach/sdk-ui/data-display';
import { isApiError, type admin } from '@repletoryreach/sdk-api';
import { useAdminLanguages } from '../../../data/useLanguages';
import { StatCard } from '../../../components/StatCard';
import { usePersistedState } from '../../../lib/usePersistedState';
import { formatInteger } from '../../../lib/format';
import { useT } from '../../../i18n/I18nProvider';
import { ViewToggle } from '../components/ViewToggle';
import { IconGlobe, IconLayers, IconSpark, IconTag } from '../components/icons';
import { byRegion, linesInCode, missingLines, sortLanguages } from './languages';
import { LanguagesTable } from './components/LanguagesTable';
import { LanguagesList } from './components/LanguagesList';
import { LanguageEditDrawer } from './components/LanguageEditDrawer';
import { useLanguageSwitch } from './components/useLanguageSwitch';
import '../components/billing.css';
import './languages.css';

const STAT_COLS = { base: 1, sm: 2, lg: 4 };

/** A phone opens on the list the first time; after that the operator's own choice is remembered. */
const FIRST_VIEW: ViewMode = typeof window !== 'undefined' && window.matchMedia?.('(max-width: 720px)').matches ? 'list' : 'table';

/**
 * The languages chatbots can speak (1 November 2026: "bot supporting multiple languages but also using the same
 * knowledge base"). A business offers its customers any language switched on here, from Settings, Chatbot, in the
 * customer console. Each row says how well the AI writes it, whether WhatsApp templates and voice notes can be in it,
 * how many chatbots offer it and how far its built-in lines (the chatbot's own fixed wording) are translated. Built-in
 * lines opens one language's lines beside the English to correct them and mark them as read by a native speaker.
 */
export default function LanguagesPage() {
  const t = useT();
  const navigate = useNavigate();
  const languagesQ = useAdminLanguages();
  const [view, setView] = usePersistedState<ViewMode>('admin.languages.view', FIRST_VIEW);
  const [editing, setEditing] = useState<admin.AdminLanguage | null>(null);
  const { busy, toggle } = useLanguageSwitch();

  const languages = useMemo(() => sortLanguages(languagesQ.data?.data ?? []), [languagesQ.data]);
  const groups = useMemo(() => byRegion(languages), [languages]);

  const stats = useMemo(() => {
    const on = languages.filter((l) => l.enabled);
    const translatable = on.filter((l) => !linesInCode(l.code));
    return {
      on: on.length,
      total: languages.length,
      offered: languages.filter((l) => l.bots_offering > 0).length,
      choices: languages.reduce((sum, l) => sum + l.bots_offering, 0),
      missing: translatable.reduce((sum, l) => sum + missingLines(l), 0),
      lines: translatable.reduce((sum, l) => sum + l.wordings.total, 0),
      review: on.filter((l) => l.quality === 'review').length,
      stale: languages.reduce((sum, l) => sum + l.wordings.stale, 0)
    };
  }, [languages]);

  const denied = isApiError(languagesQ.error) && languagesQ.error.status === 403;
  const rowProps = {
    busy,
    onToggle: toggle,
    onEdit: (l: admin.AdminLanguage) => setEditing(l),
    onLines: (l: admin.AdminLanguage) => navigate(`/billing/languages/${encodeURIComponent(l.code)}`)
  };

  return (
    <Stack gap="lg">
      <PageHeader
        eyebrow={t('billing.languages.eyebrow')}
        title={t('billing.languages.title')}
        description={t('billing.languages.description')}
        actions={
          <Group gap="sm">
            <Button size="sm" variant="outline" onClick={() => navigate('/billing/ai-models')}>{t('billing.languages.openModels')}</Button>
            <ViewToggle value={view === 'cards' ? 'table' : view} onChange={setView} />
          </Group>
        }
      />

      <SimpleGrid cols={STAT_COLS} spacing="md">
        <StatCard
          withRule tone="success" icon={<IconGlobe />}
          label={t('billing.languages.stat.on')}
          value={formatInteger(stats.on)}
          hint={t('billing.languages.stat.onHint', { total: formatInteger(stats.total) })}
          loading={languagesQ.isLoading}
        />
        <StatCard
          withRule tone="primary" icon={<IconLayers />}
          label={t('billing.languages.stat.offered')}
          value={formatInteger(stats.offered)}
          hint={t('billing.languages.stat.offeredHint', { count: formatInteger(stats.choices) })}
          loading={languagesQ.isLoading}
        />
        <StatCard
          withRule tone={stats.missing > 0 ? 'warning' : 'neutral'} icon={<IconSpark />}
          label={t('billing.languages.stat.missing')}
          value={formatInteger(stats.missing)}
          hint={t('billing.languages.stat.missingHint', { total: formatInteger(stats.lines) })}
          loading={languagesQ.isLoading}
        />
        <StatCard
          withRule tone={stats.review > 0 || stats.stale > 0 ? 'warning' : 'neutral'} icon={<IconTag />}
          label={t('billing.languages.stat.review')}
          value={formatInteger(stats.review)}
          hint={stats.stale > 0
            ? t('billing.languages.stat.staleHint', { count: formatInteger(stats.stale) })
            : t('billing.languages.stat.reviewHint')}
          loading={languagesQ.isLoading}
        />
      </SimpleGrid>

      <TableShell
        totalLabel={t('billing.languages.showingCount', { on: formatInteger(stats.on), total: formatInteger(stats.total) })}
        loading={languagesQ.isLoading}
        isEmpty={!languagesQ.error && languages.length === 0}
        empty={<EmptyState title={t('billing.languages.empty')} description={t('billing.languages.emptyHint')} />}
        error={
          languagesQ.error ? (
            <Alert color="error">
              <Stack gap="sm">
                <Text role="bodySmall">{denied ? t('billing.languages.denied') : t('billing.languages.loadError')}</Text>
                {!denied && (
                  <Group>
                    <Button size="xs" variant="outline" onClick={() => void languagesQ.refetch()}>{t('action.retry')}</Button>
                  </Group>
                )}
              </Stack>
            </Alert>
          ) : null
        }
      >
        <Stack gap="lg">
          {groups.map(({ region, languages: rows }) => (
            <Stack key={region} gap="sm" data-testid={`languages-${region}`}>
              <Group gap="xs" align="center">
                <Title order={5}>{t(`billing.languages.region.${region}`, region)}</Title>
                <Text role="caption" c="var(--rr-color-text-muted)">
                  {t('billing.languages.regionCount', { on: formatInteger(rows.filter((l) => l.enabled).length), total: formatInteger(rows.length) })}
                </Text>
              </Group>
              {view === 'list'
                ? <LanguagesList languages={rows} {...rowProps} />
                : <LanguagesTable languages={rows} {...rowProps} />}
            </Stack>
          ))}
          <Text role="caption" c="var(--rr-color-text-muted)">{t('billing.languages.note')}</Text>
        </Stack>
      </TableShell>

      <LanguageEditDrawer language={editing} onClose={() => setEditing(null)} />
    </Stack>
  );
}
